import { Box, Grid, TextField, Typography } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import LoadingIcon from "../../../../components/LoadingIcon";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";

import MongoAtlas_step_1 from "./images/MongoAtlas_step_1.png";
import MongoAtlas_step_2 from "./images/MongoAtlas_step_2.png";
import MongoAtlas_step_3 from "./images/MongoAtlas_step_3.png";
import MongoAtlas_step_4 from "./images/MongoAtlas_step_4.png";

import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import GuidelineInstruction from "../components/GuidelineInstruction";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";

const MongoAtlasIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("mongoatlas");
  const [loading, setLoading] = useState(false);
  const [publicKey, setPublicKey] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const queryClient = useQueryClient();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerMongoAtlasIntegration } = useAPI();

  const registerIntegration = () => {
    registerMongoAtlasIntegration(publicKey, privateKey)
      .then((r) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Integration registered successfully");
        setLoading(false);
      })
      .catch((e) => {
        Toast.error(
          "Failed to register integration. Please check that all your details are correct."
        );
      });
  };

  if (loading || isIntegratedLoading) {
    return <LoadingIcon />;
  }

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, Breeze will scan your Mongo Atlas for databases and collections to correlate security gaps with your business assets."
            : ""
        }
        image={logos.MongoAtlasLogo}
        name={"Mongo Atlas"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Fragment>
              <Typography variant={"h4"}>
                Mongo Atlas is already integrated!
              </Typography>
            </Fragment>
          ) : (
            <>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Connect to your Mongo Atlas environment" />
                <GuidelineStep title="Create API Key for Breeze">
                  <GuidelineInstruction text="Open your organization settings in the gear icon at the top right" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: MongoAtlas_step_1,
                        altext: "Create a new app integration",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="Go to Access Manager and create a new API Key" />

                  <ScreenshotAccordion
                    images={[
                      {
                        file: MongoAtlas_step_2,
                        altext: "Create a new app integration",
                      },
                      {
                        file: MongoAtlas_step_3,
                        altext: "Create a new app integration",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="Under permissions, choose Organization Read Only" />
                </GuidelineStep>
                <GuidelineStep title="Save the app"></GuidelineStep>
                <GuidelineStep title="Get the customer key and secret">
                  <ScreenshotAccordion
                    images={[
                      {
                        file: MongoAtlas_step_4,
                        altext: "Create a new app integration",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Fill the client ID and secret">
                  <Grid sx={{ width: "700px" }} spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="filled"
                        name="Public Key"
                        label="Public Key"
                        value={publicKey}
                        onChange={(e) => setPublicKey(e.target.value)}
                        autoComplete="off"
                        disabled={guideMode}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: "10px" }}>
                      <TextField
                        fullWidth
                        variant="filled"
                        name="Private Key"
                        label="Private Key"
                        value={privateKey}
                        onChange={(e) => setPrivateKey(e.target.value)}
                        autoComplete="off"
                        disabled={guideMode}
                      />
                    </Grid>
                  </Grid>
                </GuidelineStep>
                <GuidelineStep title="Authorize Breeze app to MongoAtlas with a connected app">
                  <PrimaryButton
                    onClick={registerIntegration}
                    disabled={guideMode}
                  >
                    Integrate
                  </PrimaryButton>
                </GuidelineStep>
              </GuidelineStepByStep>
            </>
          )
        }
      />
    </Box>
  );
};

export default MongoAtlasIntegration;
